/**
 * Pantalla 4 (docs/FASE-1.md §6): catálogo de planes de un módulo, alcanzado desde
 * `ModuleDetail`. Alta y edición de plan, con sus features y límites (según el manifiesto del
 * módulo, que define qué claves existen y de qué tipo es cada una).
 */
import type { BillingModel, CreatePlanInput, FeatureValue, Module, Plan, PlanStatus, UpdatePlanInput } from "@continnua/contracts";
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";

import { plansApi } from "../api/client";
import { Badge, toneForStatus } from "../components/Badge";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Field } from "../components/Field";
import { Modal } from "../components/Modal";
import { toast } from "../components/Toast";
import { toDisplayMessage } from "../lib/errors";
import { formatCurrency, formatNumber } from "../lib/format";

const BILLING_MODEL_LABEL: Record<BillingModel, string> = { flat: "Tarifa plana", per_seat: "Por asiento" };
const PLAN_STATUS_LABEL: Record<PlanStatus, string> = { active: "Activo", legacy: "Legado", hidden: "Oculto" };

function defaultFeatureValue(valueType: "boolean" | "number" | "string"): FeatureValue {
  if (valueType === "boolean") return false;
  if (valueType === "number") return 0;
  return "";
}

function PlanFormModal({
  module,
  existing,
  onClose,
  onSaved,
}: {
  module: Module;
  existing: Plan | null;
  onClose: () => void;
  onSaved: (plan: Plan) => void;
}): ReactElement {
  const [code, setCode] = useState(existing?.code ?? "");
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [priceMonthly, setPriceMonthly] = useState(existing?.priceMonthly ?? "0.00");
  const [currency, setCurrency] = useState(existing?.currency ?? "PEN");
  const [billingModel, setBillingModel] = useState<BillingModel>(existing?.billingModel ?? "flat");
  const [status, setStatus] = useState<PlanStatus>(existing?.status ?? "active");
  const [sortOrder, setSortOrder] = useState(existing?.sortOrder ?? 0);
  const [features, setFeatures] = useState<Record<string, FeatureValue>>(() => {
    const initial: Record<string, FeatureValue> = {};
    for (const feature of module.features) initial[feature.key] = existing?.features[feature.key] ?? defaultFeatureValue(feature.valueType);
    return initial;
  });
  const [limits, setLimits] = useState<Record<string, number | null>>(() => {
    const initial: Record<string, number | null> = {};
    for (const limit of module.limits) initial[limit.key] = existing?.limits[limit.key] ?? null;
    return initial;
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (existing) {
        const input: UpdatePlanInput = { name, description: description || null, priceMonthly, currency, billingModel, status, sortOrder, features, limits };
        const plan = await plansApi.update(module.code, existing.id, input);
        onSaved(plan);
      } else {
        const input: CreatePlanInput = {
          code,
          name,
          description: description || null,
          priceMonthly,
          currency,
          billingModel,
          status,
          sortOrder,
          features,
          limits,
        };
        const plan = await plansApi.create(module.code, input);
        onSaved(plan);
      }
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={existing ? `Editar plan · ${existing.name}` : "Nuevo plan"} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field label="Código" hint="snake_case; no se puede cambiar luego.">
          {(props) => <input {...props} required disabled={Boolean(existing)} pattern="[a-z][a-z0-9_]{1,47}" value={code} onChange={(e) => setCode(e.target.value)} />}
        </Field>
        <Field label="Nombre">{(props) => <input {...props} required value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        <Field label="Descripción" hint="Opcional.">
          {(props) => <textarea {...props} rows={2} value={description ?? ""} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        <Field label="Precio mensual" hint="Formato decimal, ej. 199.90.">
          {(props) => <input {...props} required pattern="\d+(\.\d{1,2})?" value={priceMonthly} onChange={(e) => setPriceMonthly(e.target.value)} />}
        </Field>
        <Field label="Moneda">{(props) => <input {...props} required maxLength={3} minLength={3} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />}</Field>
        <Field label="Modelo de facturación">
          {(props) => (
            <select {...props} value={billingModel} onChange={(e) => setBillingModel(e.target.value as BillingModel)}>
              <option value="flat">Tarifa plana</option>
              <option value="per_seat">Por asiento</option>
            </select>
          )}
        </Field>
        <Field label="Estado">
          {(props) => (
            <select {...props} value={status} onChange={(e) => setStatus(e.target.value as PlanStatus)}>
              <option value="active">Activo</option>
              <option value="legacy">Legado</option>
              <option value="hidden">Oculto</option>
            </select>
          )}
        </Field>
        <Field label="Orden">{(props) => <input {...props} type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />}</Field>

        {module.features.length > 0 && (
          <fieldset className="roles-fieldset">
            <legend>Features</legend>
            {module.features.map((feature) => {
              const value = features[feature.key] ?? defaultFeatureValue(feature.valueType);
              const id = `plan-feature-${feature.key}`;
              return (
                <div key={feature.key} className="override-row">
                  {feature.valueType === "boolean" && (
                    <label className="checkbox-label" htmlFor={id}>
                      <input
                        id={id}
                        type="checkbox"
                        checked={Boolean(value)}
                        onChange={(e) => setFeatures((prev) => ({ ...prev, [feature.key]: e.target.checked }))}
                      />
                      {feature.name}
                    </label>
                  )}
                  {feature.valueType === "number" && (
                    <Field label={feature.name}>
                      {(props) => (
                        <input
                          {...props}
                          type="number"
                          value={typeof value === "number" ? value : 0}
                          onChange={(e) => setFeatures((prev) => ({ ...prev, [feature.key]: Number(e.target.value) }))}
                        />
                      )}
                    </Field>
                  )}
                  {feature.valueType === "string" && (
                    <Field label={feature.name}>
                      {(props) => (
                        <input
                          {...props}
                          type="text"
                          value={typeof value === "string" ? value : ""}
                          onChange={(e) => setFeatures((prev) => ({ ...prev, [feature.key]: e.target.value }))}
                        />
                      )}
                    </Field>
                  )}
                </div>
              );
            })}
          </fieldset>
        )}

        {module.limits.length > 0 && (
          <fieldset className="roles-fieldset">
            <legend>Límites</legend>
            {module.limits.map((limit) => (
              <Field key={limit.key} label={`${limit.name} (${limit.unit})`} hint="Vacío = ilimitado.">
                {(props) => (
                  <input
                    {...props}
                    type="number"
                    min={0}
                    value={limits[limit.key] ?? ""}
                    onChange={(e) => setLimits((prev) => ({ ...prev, [limit.key]: e.target.value === "" ? null : Number(e.target.value) }))}
                  />
                )}
              </Field>
            ))}
          </fieldset>
        )}

        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ModulePlans({ module }: { module: Module }): ReactElement {
  const [items, setItems] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await plansApi.list(module.code);
      setItems(page.items);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setLoading(false);
    }
  }, [module.code]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: DataTableColumn<Plan>[] = [
    { key: "code", header: "Código", render: (p) => p.code },
    { key: "name", header: "Nombre", render: (p) => p.name },
    { key: "price", header: "Precio", render: (p) => formatCurrency(p.priceMonthly, p.currency) },
    { key: "billing", header: "Facturación", render: (p) => BILLING_MODEL_LABEL[p.billingModel] },
    { key: "status", header: "Estado", render: (p) => <Badge tone={toneForStatus(p.status)}>{PLAN_STATUS_LABEL[p.status]}</Badge> },
    { key: "sortOrder", header: "Orden", render: (p) => formatNumber(p.sortOrder) },
    {
      key: "actions",
      header: "Acciones",
      render: (p) => (
        <button type="button" className="button-secondary button-small" onClick={() => setEditing(p)}>
          Editar
        </button>
      ),
    },
  ];

  return (
    <section>
      <div className="page-header">
        <h2>Planes</h2>
        <button type="button" className="button-primary" onClick={() => setShowCreate(true)}>
          Nuevo plan
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <DataTable columns={columns} rows={items} rowKey={(p) => p.id} emptyMessage={loading ? "Cargando…" : "No hay planes registrados."} />
      {showCreate && (
        <PlanFormModal
          module={module}
          existing={null}
          onClose={() => setShowCreate(false)}
          onSaved={(plan) => {
            setShowCreate(false);
            setItems((prev) => [plan, ...prev]);
            toast.success("Plan creado.");
          }}
        />
      )}
      {editing && (
        <PlanFormModal
          module={module}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={(plan) => {
            setEditing(null);
            setItems((prev) => prev.map((p) => (p.id === plan.id ? plan : p)));
            toast.success("Plan actualizado.");
          }}
        />
      )}
    </section>
  );
}
